import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";

import { PageHero, Section } from "@/components/site/section";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  company: z.string().trim().min(1).max(120),
  contact_name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

export const Route = createFileRoute("/contact-us/partnership")({
  head: () => ({
    meta: [
      { title: "Become Our Partner | Argentinian.Soccer" },
      { name: "description", content: "Sponsorship, syndication and integration partnerships with Argentinian.Soccer." },
      { property: "og:title", content: "Become Our Partner | Argentinian.Soccer" },
      { property: "og:url", content: "/contact-us/partnership" },
    ],
    links: [{ rel: "canonical", href: "/contact-us/partnership" }],
  }),
  component: PartnerPage,
});

function PartnerPage() {
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse(Object.fromEntries(fd));
    if (!parsed.success) return toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
    setSubmitting(true);
    const { error } = await supabase.from("partnership_inquiries").insert({
      company: parsed.data.company,
      contact_name: parsed.data.contact_name,
      email: parsed.data.email,
      message: parsed.data.message || null,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Thanks — we'll be in touch.");
    e.currentTarget.reset();
  }

  return (
    <>
      <PageHero eyebrow="Partnerships" title="Become Our Partner" />
      <Section>
        <form onSubmit={onSubmit} className="max-w-2xl space-y-4 rounded-lg border border-border bg-card p-6">
          <Input label="Company" name="company" required />
          <Input label="Contact name" name="contact_name" required />
          <Input label="Email" name="email" type="email" required />
          <div>
            <label className="mb-1 block text-sm font-medium">How can we help?</label>
            <textarea name="message" rows={5} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <button
            disabled={submitting}
            className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Submit inquiry"}
          </button>
        </form>
      </Section>
    </>
  );
}

function Input({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <input {...props} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
    </div>
  );
}
